import { useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { queryClient } from 'providers/AllProviders';
import { MetaApiResponse } from 'utils/types/CommonApiResponse';
import Cookies from 'js-cookie';

import {
  OnboardingFormRequest,
  OnboardingFormResponse,
  HistoryLog,
  BusinessAutocompleteResponse,
  BusinessPlaceDetails,
  PublicOnboardingFormRequest
} from './types';
import {
  createOnboardingForm,
  archiveOnboardingForm,
  getOnboardingForm,
  getOnboardingForms,
  getOnboardingFormHistories,
  getPublicOnboardingForm,
  loginOnboardingForm,
  updateOnboardingForm,
  updatePublicOnboardingForm,
  getPublicCountries,
  getPublicStates,
  getPublicAvailableIntegrations,
  approveOnboardingForm,
  getBusinessAutocomplete,
  getBusinessPlaceDetails,
  createPublicOnboardingForm,
  createCheckoutSession,
  createPromoCode,
  validateIntegrationCredentials,
  getOnboardingPostConfigurationFields,
  PublicAvailableIntegrationResponse,
  OnboardingFormLoginRequest,
  OnboardingFormLoginResponse
} from './apis';

export function useGetAgencyOnboardingForms(
  page: number,
  filters?: Record<string, any>,
  options?: Omit<
    UseQueryOptions<{ data: OnboardingFormResponse[]; meta: MetaApiResponse }, Error>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery<{ data: OnboardingFormResponse[]; meta: MetaApiResponse }>({
    queryKey: ['agencyOnboardingForms', page, filters],
    queryFn: () => getOnboardingForms(page, filters),
    ...options
  });
}

export function useGetAgencyOnboardingForm(options?: UseQueryOptions<OnboardingFormResponse, Error>) {
  const { onboardingFormId } = useParams<{ onboardingFormId: string }>();

  return useQuery<OnboardingFormResponse>({
    queryKey: ['agencyOnboardingForm', onboardingFormId],
    queryFn: () => getOnboardingForm(onboardingFormId!),
    enabled: Boolean(onboardingFormId),
    ...options
  });
}

export function useCreateOrUpdateAgencyOnboardingForm(isModalClose?: boolean) {
  const navigate = useNavigate();
  const { onboardingFormId } = useParams<{ onboardingFormId: string }>();
  const currentFormId = Cookies.get('onboarding_id');
  const onboardId = onboardingFormId || currentFormId;

  return useMutation({
    mutationFn: (
      variables:
        | OnboardingFormRequest
        | FormData
        | { payload: OnboardingFormRequest | FormData; skipSuccessToast?: boolean }
    ) => {
      const isWrapped =
        typeof variables === 'object' &&
        variables !== null &&
        !(variables instanceof FormData) &&
        'payload' in variables &&
        !('onboarding_form' in variables);

      const payload = isWrapped
        ? (variables as { payload: OnboardingFormRequest | FormData }).payload
        : (variables as OnboardingFormRequest | FormData);
      const skipSuccessToast = isWrapped ? (variables as { skipSuccessToast?: boolean }).skipSuccessToast : undefined;

      return onboardId
        ? updateOnboardingForm(onboardId!, payload, { skipSuccessToast })
        : createOnboardingForm(payload);
    },

    onSuccess: (data) => {
      if (data !== undefined) {
        queryClient.invalidateQueries({
          queryKey: ['agencyOnboardingForm'],
          refetchType: 'active'
        });
        queryClient.refetchQueries({
          queryKey: ['agencyOnboardingForms']
        });
        if (isModalClose) navigate(-1);
      }
    }
  });
}

export function useCreatePublicOnboardingForm() {
  return useMutation({
    mutationFn: (payload: PublicOnboardingFormRequest | FormData) => createPublicOnboardingForm(payload)
  });
}

export function useArchiveAgencyOnboardingForm() {
  return useMutation({
    mutationFn: (id: string) => archiveOnboardingForm(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['agencyOnboardingForms'],
        refetchType: 'active'
      });
    }
  });
}

export function useGetAgencyOnboardingFormHistories(
  id: string,
  options?: UseQueryOptions<{ data: HistoryLog[]; links?: any; meta?: any }, Error>
) {
  return useQuery<{ data: HistoryLog[]; links?: any; meta?: any }>({
    queryKey: ['agencyOnboardingFormHistories', id],
    queryFn: () => getOnboardingFormHistories(id),
    enabled: Boolean(id),
    ...options
  });
}

export function useLoginOnboardingForm() {
  return useMutation<OnboardingFormLoginResponse, Error, OnboardingFormLoginRequest>({
    mutationFn: (payload: OnboardingFormLoginRequest) => loginOnboardingForm(payload)
  });
}

export function useGetPublicOnboardingForm(
  formId: string | undefined,
  options?: Omit<UseQueryOptions<OnboardingFormResponse, Error>, 'queryKey' | 'queryFn'>
) {
  const isIdValid = !!formId && formId !== 'undefined';
  return useQuery<OnboardingFormResponse>({
    queryKey: ['publicOnboardingForm', formId],
    queryFn: () => getPublicOnboardingForm(formId!),
    ...options,
    enabled: isIdValid && (options?.enabled !== undefined ? options.enabled : true)
  });
}

export function useUpdatePublicOnboardingForm() {
  return useMutation<
    OnboardingFormResponse,
    Error,
    { formId: string; payload: OnboardingFormRequest | FormData; skipSuccessToast?: boolean }
  >({
    mutationFn: ({ formId, payload, skipSuccessToast }) =>
      updatePublicOnboardingForm(formId, payload, { skipSuccessToast }),
    onSuccess: (data, variables) => {
      if (data !== undefined) {
        // Token is regenerated server-side when email/phone change (NetSapiens forms); it must be
        // stored before invalidation so the refetch and subsequent calls authenticate with it.
        if (data.token) {
          Cookies.set('auth_token', data.token, { expires: 30 });
        }
        queryClient.setQueryData(['publicOnboardingForm', variables.formId], data);
        queryClient.invalidateQueries({
          queryKey: ['publicOnboardingForm', variables.formId],
          refetchType: 'active'
        });
      }
    }
  });
}

export function useGetPublicCountries(options?: UseQueryOptions<{ value: string | number; label: string }[], Error>) {
  return useQuery<{ value: string | number; label: string }[]>({
    queryKey: ['publicCountries'],
    queryFn: () => getPublicCountries(),
    ...options
  });
}

export function useGetPublicStates(
  countryCode: string,
  options?: UseQueryOptions<{ value: string | number; label: string }[], Error>
) {
  return useQuery<{ value: string | number; label: string }[]>({
    queryKey: ['publicStates', countryCode],
    queryFn: () => getPublicStates(countryCode),
    enabled: Boolean(countryCode),
    ...options
  });
}

export function useGetPublicAvailableIntegrations(
  params?: Record<string, any>,
  options?: Omit<
    UseQueryOptions<{ data: PublicAvailableIntegrationResponse[]; meta?: any }, Error>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery<{ data: PublicAvailableIntegrationResponse[]; meta?: any }>({
    queryKey: ['publicAvailableIntegrations', params],
    queryFn: () => getPublicAvailableIntegrations(params),
    ...options
  });
}

export function useApproveOnboardingForm() {
  return useMutation<OnboardingFormResponse, Error, string>({
    mutationFn: (id: string) => approveOnboardingForm(id),
    onSuccess: () => {
      queryClient.refetchQueries({
        queryKey: ['agencyOnboardingForms']
      });
      queryClient.refetchQueries({
        queryKey: ['agencyOnboardingForm']
      });
    }
  });
}

export function useDeleteOnboardingFile() {
  const onboardingId = Cookies.get('onboarding_id');
  const isBusinessOnboardingStage2 = location.pathname.includes('/business-onboarding/stage-2');
  const { mutate: updateAgency } = useCreateOrUpdateAgencyOnboardingForm(false);
  const { mutate: updatePublic } = useUpdatePublicOnboardingForm();
  const deleteFile = (id: number | string, attribute_name: string = 'knowledgebase_files_attributes') => {
    const payload: any = {
      [attribute_name]: [
        {
          id,
          _destroy: true
        }
      ]
    };
    if (isBusinessOnboardingStage2) {
      updatePublic({
        formId: onboardingId!,
        payload
      });
    } else {
      updateAgency(payload);
    }
  };

  return { deleteFile };
}

export function useGetBusinessAutocomplete(
  input: string,
  options?: UseQueryOptions<BusinessAutocompleteResponse, Error>
) {
  return useQuery<BusinessAutocompleteResponse, Error>({
    queryKey: ['businessAutocomplete', input],
    queryFn: () => getBusinessAutocomplete(input),
    enabled: Boolean(input && input.length >= 3),
    ...options
  });
}

export function useGetBusinessPlaceDetails() {
  return useMutation<BusinessPlaceDetails, Error, string>({
    mutationFn: (placeId: string) => getBusinessPlaceDetails(placeId)
  });
}

export function useCreateCheckoutSession() {
  return useMutation({
    mutationFn: (payload: any) => createCheckoutSession(payload)
  });
}

export function useCreatePromoCode() {
  return useMutation({
    mutationFn: (id: string) => createPromoCode(id)
  });
}

export function useValidateIntegrationCredentials() {
  return useMutation({
    mutationFn: validateIntegrationCredentials
  });
}

export function useGetOnboardingPostConfigurationFields(
  integrationCode?: string,
  configuration?: Record<string, unknown>,
  enabled?: boolean,
  isPublicRoute?: boolean,
  fetchKey?: number,
  options?: Omit<UseQueryOptions<any, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery<any>({
    queryKey: ['onboarding_post_configs', integrationCode, fetchKey ?? 0, isPublicRoute],
    queryFn: () =>
      getOnboardingPostConfigurationFields({
        code: integrationCode!,
        configuration: configuration || {},
        isPublicRoute: Boolean(isPublicRoute)
      }),
    enabled: Boolean(integrationCode && enabled),
    ...options
  });
}
